import { useState, useEffect, useMemo, useCallback } from 'react'
import { DirectoryButtonProp } from '../../Molecules/LeksikonDirectory/props'
import { Result } from '../../Molecules/LeksikonSearchResult/props'
import {
    Directory,
    DirectoryChildren,
    DirectoryWithIcon,
    Node,
} from '../../Types/LeksikonTypes'
import { Seed, TreeSeeder } from '../../Types/LeksikonTypes/treeSeeder'

export const useLeksikon = (
    searchTitle: string,
    seeds: Seed[],
    setView: (e: string) => void,
    view: string
) => {
    const [viewKey, setViewKey] = useState('/lexicon')

    const [searchedText, setSearchedText] = useState('')
    const [highLightedText, setHighLightedText] = useState<string>()

    useEffect(() => {
        setViewKey(view)
    }, [view])

    const root = useMemo(
        () => TreeSeeder.plantTree(Object.values(seeds)),
        [seeds]
    )

    const node = root.findChildBySlug(viewKey + '')

    const handleNavigation = useCallback(
        (newViewKey: string) => {
            setView(newViewKey)
            setHighLightedText(undefined)
        },
        [setView]
    )

    const onClickResult = useCallback(
        (thisnode: Node<any>) => {
            handleNavigation(thisnode.slug)
            setHighLightedText(searchedText)
            setSearchedText('')
        },
        [handleNavigation, searchedText]
    )

    const breadCrumbs = useMemo(() => {
        const slugs = viewKey.split('/').slice(1)
        return node
            .getBreadcrumbs()
            .reverse()
            .map((breadcrumb, i) => {
                return {
                    onClick: () => {
                        const newViewKey = [...slugs]
                            .reverse()
                            .slice(slugs.length - i - 1)
                            .reverse()
                            .join('/')
                        handleNavigation(`/${newViewKey}`)
                    },
                    label: breadcrumb.title,
                }
            })
    }, [handleNavigation, node, viewKey])

    const createDirectoryButtons = useCallback(
        (parent: Directory<DirectoryChildren>): DirectoryButtonProp[] =>
            Array.isArray(parent.content)
                ? parent.content.map((thisnode) => ({
                      text: thisnode.title,
                      onClick: () => handleNavigation(`${thisnode.slug}`),
                  }))
                : [
                      {
                          text: parent.title,
                          onClick: () => handleNavigation(`${parent.slug}`),
                      },
                  ],
        [handleNavigation]
    )

    const searchResults = useMemo(
        () =>
            root.search(searchedText).map<Result>((result) => ({
                title: result.title,
                text: result.type === 'document' ? result.content : '',
                onClick: () => onClickResult(result),
            })),
        [onClickResult, root, searchedText]
    )

    const onChangeSearch = (value: string) => setSearchedText(value)

    const buttonsOfDirectoryWithIcons = useCallback(
        () =>
            (node.content as DirectoryWithIcon[]).map(
                ({ title, slug, icon }) => {
                    const capitalizedIconName =
                        icon.name.charAt(0).toUpperCase() + icon.name.slice(1)

                    return {
                        text: title,
                        icon: capitalizedIconName,
                        onClick: () => handleNavigation(`${slug}`),
                    }
                }
            ),
        [handleNavigation, node.content]
    )

    const content = useMemo(() => {
        if (node.parent === 'ROOT') {
            return {
                type: 'DirectoryWithIcons',
                title: node.title,
                buttons: buttonsOfDirectoryWithIcons(),
                searchResults: {
                    searchedText: searchedText,
                    results: searchResults,
                    title: searchTitle,
                },
            }
        } else if (node.type === 'document') {
            return {
                type: 'Document',
                title: node.title,
                content: node.content,
                breadcrumbs: breadCrumbs,
                highLightedText: highLightedText,
            }
        } else if (
            node.type === 'directory' ||
            node.type === 'directoryWithIcon'
        ) {
            const nodes =
                node.content[0].type === 'document' ? [node] : node.content

            return {
                type: 'Directory',
                title: node.title,
                section: nodes.map((n: any) => ({
                    title: n.title,
                    buttons: createDirectoryButtons(
                        n as Directory<DirectoryChildren>
                    ),
                })),
                searchResults: {
                    searchedText: searchedText,
                    results: searchResults,
                    title: searchTitle,
                },
            }
        }

        throw new Error('This node type is not implemented yet.')
    }, [
        breadCrumbs,
        buttonsOfDirectoryWithIcons,
        createDirectoryButtons,
        searchResults,
        highLightedText,
        node,
        searchTitle,
        searchedText,
    ])

    return { content, onChangeSearch }
}
